import { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';
import { Clock, Coins, ArrowRight, X, Loader2, HelpCircle, ClipboardList, Smile, Flame, Check } from 'lucide-react';
import {
  assignSurvey, startPsSurvey, startRfgSurvey, prescreenSurvey, answerSurveyQual,
  submitPrescreenAnswer, fetchPrescreenBuckets, fetchProfileEnrichmentQuestion,
  saveMemberAttribute, type PrescreenBucket, type ProfileEnrichmentQuestion,
} from '../lib/api';
import { trackFirstSurveyStart } from '../lib/analytics';
import { setSurveyTabHandle, setSurveyEntryUrl, shouldLaunchSameTab } from '../lib/surveyTabHandle';
import { PRESCREEN_STATEMENTS } from '../lib/prescreenCategories';
import { useMe } from '../hooks/useProfile';
import { useRankedSurveys } from '../hooks/useSurveys';
import { scoreSurveys, getLoi, getMatchTier, matchTierPillClasses } from '../lib/surveyRanking';
import type { RankedSurvey, UnknownQual } from '@swipeopinion/shared';

interface PrescreenQ {
  qualCode: number; questionText: string; questionType: string;
  options: Array<{ label: string; conditionCodes: string[]; value: string }>;
  surveysUnlocked: number; attributeKey: string;
}

interface Props {
  survey: RankedSurvey;
  onClose: () => void;
  onDeny?: () => void;
  onSurveyUnavailable?: (surveyId: string) => void;
  /** Called the instant a survey start succeeds, before navigation. Lets the
   *  parent invalidate the shared ['surveys'] query (and optimistically dismiss
   *  the card) so the started survey leaves the wall immediately instead of
   *  lingering until React Query's staleTime elapses. */
  onStarted?: (surveyId: string) => void;
  /** Called after the qual gate disqualifies the member; parent removes the
   *  survey from the wall in local state so the wall doesn't refetch. */
  onDisqualified?: (surveyId: string) => void;
  /** Called when the member taps "Try this one" on the soft-landing screen —
   *  parent swaps the modal to the next survey (remount via key). */
  onTryNext?: (next: RankedSurvey) => void;
  prescreenQuestion?: PrescreenQ | null;
  onPrescreenAnswer?: (qualCode: number, conditionCodes: string[], value: string, attributeKey: string) => void;
  onPrescreenSkip?: (qualCode: number) => void;
}

const CATEGORY_TIPS: Record<string, string> = {
  Technology: 'You may be asked about devices or software you use',
  Healthcare: 'Questions may relate to health conditions or medications',
  Health: 'Questions may relate to health conditions or medications',
  Finance: 'Questions may relate to banking or investment habits',
  'CPG/Food': 'Questions may relate to grocery or food preferences',
  'Food & Dining': 'Questions may relate to grocery or food preferences',
  Auto: 'Questions may relate to vehicle ownership or purchasing',
  Travel: 'Questions may relate to travel habits or preferences',
};

export default function SurveyStartModal({ survey, onClose, onDeny, onSurveyUnavailable, onStarted, onDisqualified, onTryNext, prescreenQuestion, onPrescreenAnswer, onPrescreenSkip }: Props) {
  const navigate = useNavigate();
  const [launching, setLaunching] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  // Soft-landing data (T2): real streak + best next survey to keep momentum.
  const { data: me } = useMe();
  // Trusted members (the full L4+ AND fraud-tier gate, computed server-side) see
  // their survey points credit instantly — surface a subtle tag near the reward.
  const instantCredit = (me as { instant_credit_eligible?: boolean } | undefined)?.instant_credit_eligible ?? false;
  const { data: rankedSurveys } = useRankedSurveys();
  const streakCurrent = me?.streak_current ?? 0;

  // Legacy prescreen (category-based yes/no)
  const [legacyAnswer, setLegacyAnswer] = useState<boolean | null>(null);
  const hasLegacyPrescreen = !isPsSurveyCheck(survey) && survey.prescreen && !!survey.category && !!PRESCREEN_STATEMENTS[survey.category];
  const legacyStatement = hasLegacyPrescreen ? PRESCREEN_STATEMENTS[survey.category!] : null;

  // Inline qual-gate prescreen (formerly PS-only — now exchange-agnostic).
  // PS unknownQuals submit via the legacy /prescreen endpoint (qualCode +
  // conditionCode); RFG unknownQuals submit via /answer-qual (attributeKey
  // + attributeValue). The modal renders both identically.
  const [pendingQuals, setPendingQuals] = useState<UnknownQual[]>(survey.unknownQuals ?? []);
  const [currentQualIdx, setCurrentQualIdx] = useState(0);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);       // single/range/open
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);            // multi-select
  const [submittingPrescreen, setSubmittingPrescreen] = useState(false);
  // Qual-gate skip is deliberately friction-ful (T7): tapping "can't answer"
  // reveals a choice that prefers finding a better match over routing the
  // member into a likely screen-out. `skippedQualGate` persists an expectation
  // note once the member chooses to try anyway.
  const [skipExpanded, setSkipExpanded] = useState(false);
  const [skippedQualGate, setSkippedQualGate] = useState(false);

  const [prescreenFailed, setPrescreenFailed] = useState(false);
  // Consolation earned on the in-modal screenout. Pre-entry screenouts award
  // 0 (no survey time spent), so this is normally 0 and we show the
  // zero-consolation explanation (T7). Kept as state for forward-compat if the
  // qual-gate ever returns consolation.
  const [failConsolation, setFailConsolation] = useState(0);
  const [profileAnswer, setProfileAnswer] = useState<string | null>(null);
  const [profileAnswered, setProfileAnswered] = useState(false);
  const [claudeBuckets, setClaudeBuckets] = useState<PrescreenBucket[] | null>(null);
  const [bucketsLoading, setBucketsLoading] = useState(false);
  const [selectedBucketIdx, setSelectedBucketIdx] = useState<number | null>(null);
  const [multiSelectedValues, setMultiSelectedValues] = useState<string[]>([]);

  // Profile enrichment (TYPE 2) — fetched internally when no prescreenQuestion
  // prop is supplied and there's no qual gate pending. Always non-blocking.
  const [enrichmentQ, setEnrichmentQ] = useState<ProfileEnrichmentQuestion | null>(null);
  const [enrichmentLoaded, setEnrichmentLoaded] = useState(false);
  const [enrichmentValue, setEnrichmentValue] = useState<string | null>(null);
  const [enrichmentMulti, setEnrichmentMulti] = useState<string[]>([]);
  const [enrichmentSavedOrSkipped, setEnrichmentSavedOrSkipped] = useState(false);
  // Defer enrichment fetch by one render cycle so initial state is fully
  // stable. Prevents the question from flashing in if derived flags
  // (hasQualGate etc.) re-compute on the second render.
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMultiSelectedValues([]);
    if (!prescreenQuestion || prescreenQuestion.questionType !== 'claude_buckets') {
      setClaudeBuckets(null);
      setSelectedBucketIdx(null);
      return;
    }
    let cancelled = false;
    setBucketsLoading(true);
    setClaudeBuckets(null);
    setSelectedBucketIdx(null);
    setProfileAnswer(null);
    const qc = prescreenQuestion.qualCode;
    fetchPrescreenBuckets(qc)
      .then((res) => {
        if (cancelled) return;
        if (!res.buckets || res.buckets.length === 0) {
          // Empty response — auto-skip this question so the parent advances.
          onPrescreenSkip?.(qc);
          return;
        }
        setClaudeBuckets(res.buckets);
      })
      .catch(() => {
        if (cancelled) return;
        // Fetch failure — auto-skip so member can proceed.
        onPrescreenSkip?.(qc);
      })
      .finally(() => { if (!cancelled) setBucketsLoading(false); });
    return () => { cancelled = true; };
  }, [prescreenQuestion, onPrescreenSkip]);

  const isPsSurvey = isPsSurveyCheck(survey);
  // Exchange-agnostic: any survey with unknownQuals shows the qual gate.
  const hasQualGate = pendingQuals.length > 0;
  const currentQual = hasQualGate ? pendingQuals[currentQualIdx] : null;
  const pointsUsd = (survey.points_value / 100).toFixed(2);
  const categoryTip = CATEGORY_TIPS[survey.category ?? ''] ?? 'Answer honestly — quality is checked';

  // Best next survey for the soft landing — top of the ranked wall, excluding
  // the one that just screened the member out. scoreSurveys preserves the
  // backend ranking order and gives us a display title, cash value, and a
  // qualitative match label for the card.
  const bestNextSurvey = scoreSurveys((rankedSurveys ?? []).filter((s) => s.id !== survey.id))[0] ?? null;

  function handleTryNext() {
    if (!bestNextSurvey) return;
    if (onTryNext) onTryNext(bestNextSurvey);
    else onClose();
  }

  // Profile enrichment fires only when there's no qual gate, no legacy
  // prescreen, and the parent didn't already supply a prescreenQuestion.
  const wantsEnrichment = !prescreenFailed && !hasQualGate && !hasLegacyPrescreen && !prescreenQuestion;

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    if (!mounted) return;
    if (!wantsEnrichment) {
      // Gate closed after the fetch started — drop anything loaded so the
      // question doesn't flash in and out.
      if (enrichmentLoaded || enrichmentQ) {
        setEnrichmentLoaded(false);
        setEnrichmentQ(null);
      }
      return;
    }
    if (enrichmentLoaded) return;
    let cancelled = false;
    fetchProfileEnrichmentQuestion()
      .then((q) => { if (!cancelled) { setEnrichmentQ(q); setEnrichmentLoaded(true); } })
      .catch(() => { if (!cancelled) { setEnrichmentQ(null); setEnrichmentLoaded(true); } });
    return () => { cancelled = true; };
  }, [mounted, wantsEnrichment, enrichmentLoaded, enrichmentQ]);

  const showEnrichment = wantsEnrichment && enrichmentLoaded && enrichmentQ != null && !enrichmentSavedOrSkipped;

  // Can start: no prescreens remaining or legacy prescreen passed.
  // Profile enrichment is non-blocking — its mere presence does not gate.
  const showProfileQuestion = !prescreenFailed && !hasQualGate && !hasLegacyPrescreen && prescreenQuestion && !profileAnswered;
  const canStart = prescreenFailed ? false
    : hasQualGate ? false
    : hasLegacyPrescreen ? legacyAnswer === true
    : showProfileQuestion ? false  // Must answer or skip the legacy profiling question
    : true;

  function handleLegacySelect(answer: boolean) {
    setLegacyAnswer(answer);
    if (!answer) {
      // Legacy category "No" → same soft-landing screen-out as the qual gate.
      triggerDisqualifiedUx();
    }
    if (survey.category) {
      // Best-effort prescreen write — report failures to monitoring instead of
      // dropping them silently (the qualification decision is unaffected).
      void submitPrescreenAnswer(survey.category, answer).catch((err) => Sentry.captureException(err));
    }
  }

  const isMulti = currentQual?.type === 'multi';
  const hasAnswer = isMulti ? selectedCodes.length > 0 : !!selectedCode;

  function toggleMultiCode(code: string) {
    setSelectedCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  }

  function triggerDisqualifiedUx(consolation = 0) {
    setPrescreenFailed(true);
    setFailConsolation(consolation);
    setSubmittingPrescreen(false);
    // T2: NO auto-dismiss. The member leaves on their own terms via the
    // soft-landing CTAs. Remove the survey from the wall in the background so
    // the wall is fresh when they return, but keep the modal open.
    onDisqualified?.(survey.id);
  }

  async function handleQualGateAnswer() {
    if (!currentQual || !hasAnswer) return;
    setSubmittingPrescreen(true);

    const answerCode = isMulti ? selectedCodes.join(',') : selectedCode!;

    try {
      // PS unknownQuals lack `attributeKey` (the qualCode is the canonical
      // PS identifier and the legacy /prescreen endpoint resolves the attr
      // key server-side). RFG quals always carry `attributeKey` and are
      // submitted via the unified /answer-qual endpoint.
      const useUnified = currentQual.exchange === 'rfg' || !!currentQual.attributeKey;
      const result = useUnified
        ? await answerSurveyQual(survey.id, currentQual.attributeKey ?? '', answerCode)
        : await prescreenSurvey(survey.id, currentQual.qualCode, answerCode);

      if (!result.qualifies && !result.unknownQuals?.length) {
        triggerDisqualifiedUx();
        return;
      }

      // Update remaining unknowns from server response
      const remaining = (result.unknownQuals ?? pendingQuals.slice(currentQualIdx + 1)) as UnknownQual[];

      // Loop guard: if the backend returns the qual we just answered
      // back in unknownQuals, the answer didn't land (silent server-side
      // drop). Strip the stuck qual and advance so the member isn't
      // trapped re-answering the same question. The exchange's own
      // entry screener remains the authoritative qualifier.
      const currentQc = currentQual.qualCode;
      const stuck = remaining.some((q) => q.qualCode === currentQc);
      if (stuck) {
        // eslint-disable-next-line no-console
        console.warn(`[qual-gate] backend returned qualCode ${currentQc} as still unknown after answer; skipping to avoid loop`);
      }
      const safeRemaining = stuck ? remaining.filter((q) => q.qualCode !== currentQc) : remaining;

      setPendingQuals(safeRemaining);
      setCurrentQualIdx(0);
      setSelectedCode(null);
      setSelectedCodes([]);

    } catch {
      // On error, skip this qual and continue
      if (currentQualIdx + 1 < pendingQuals.length) {
        setCurrentQualIdx((i) => i + 1);
        setSelectedCode(null);
        setSelectedCodes([]);
      } else {
        setPendingQuals([]);
      }
    }

    setSubmittingPrescreen(false);
  }

  // "Try anyway" — clear the gate and proceed to start. The exchange will
  // screen the member out at entry if they don't qualify, so we persist an
  // expectation note (skippedQualGate) near the start button.
  function handleQualGateSkip() {
    setSkippedQualGate(true);
    setSkipExpanded(false);
    setPendingQuals([]);
    setCurrentQualIdx(0);
    setSelectedCode(null);
    setSelectedCodes([]);
  }

  // Preferred path when the member can't answer a required qual: suppress this
  // survey and find a better match rather than spending a likely screen-out
  // (protects supplier score). Falls back to closing if no onDeny is wired.
  function handleFindBetterMatch() {
    if (onDeny) onDeny();
    else onClose();
  }

  async function handleEnrichmentAnswer() {
    if (!enrichmentQ) return;
    const value = enrichmentQ.inputType === 'multi_select' ? enrichmentMulti.join(',') : enrichmentValue;
    if (!value) return;
    try {
      await saveMemberAttribute(enrichmentQ.attributeKey, value);
    } catch { /* non-blocking — proceed regardless */ }
    setEnrichmentSavedOrSkipped(true);
    // Spec: answering or skipping enrichment starts the survey immediately.
    void handleStart();
  }

  function handleEnrichmentSkip() {
    setEnrichmentSavedOrSkipped(true);
    void handleStart();
  }

  async function handleStart() {
    if (!canStart || launching) return;

    // Device-aware launch strategy (T1):
    //  - Desktop: open a new tab and babysit it from SurveyWaitingPage.
    //  - Mobile / in-app browsers: navigate the SAME tab full-page and rely on
    //    the provider redirect to land the member on a result route. The
    //    programmatic popup is unreliable in webviews, so we never open one.
    const sameTab = shouldLaunchSameTab();

    // Open the blank tab SYNCHRONOUSLY before any await (desktop only). Browsers
    // only allow window.open in the user-gesture chain — awaiting first breaks
    // that chain and gets the popup blocked. On the same-tab path there is no
    // popup to claim.
    const surveyWindow = sameTab ? null : window.open('', '_blank');
    const didOpen = surveyWindow != null;

    setLaunching(true);
    setStartError(null);

    try {
      let waitingId: string;
      let openUrl: string | null = null;

      if (survey.exchange === 'rfg') {
        const { entryUrl, sessionId } = await startRfgSurvey(survey.id);
        waitingId = sessionId;
        openUrl = entryUrl;
      } else if (isPsSurvey) {
        const { entryUrl, sessionId } = await startPsSurvey(survey.id);
        waitingId = sessionId;
        openUrl = entryUrl;
      } else {
        const { assignmentId, surveyUrl } = await assignSurvey(survey.id);
        waitingId = assignmentId;
        openUrl = surveyUrl;
      }

      // GA4: first_survey_start fires once per browser. sessionStorage avoids
      // duplicate events on re-renders / quick re-attempts within the same tab.
      try {
        if (!sessionStorage.getItem('so_first_survey_started')) {
          trackFirstSurveyStart();
          sessionStorage.setItem('so_first_survey_started', '1');
        }
      } catch { /* analytics non-fatal */ }

      // Mobile / in-app: same-tab full-page navigation to the survey. The
      // provider redirect (PS/RFG already credit via the redirect) brings the
      // member back to /survey/complete | /survey/screenout |
      // /survey/security-terminate, which read the outcome from the redirect
      // params — no waiting page, no tab handle, no polling.
      if (sameTab) {
        if (openUrl) {
          // Survey is now consumed server-side — drop it from the wall before
          // we leave the page so it isn't served from React Query's cache when
          // the member returns.
          onStarted?.(survey.id);
          window.location.assign(openUrl);
          return;
        }
        // No entry URL came back — surface an error rather than stranding the
        // member on a dead screen.
        setStartError('Unable to start survey — please try again');
        setLaunching(false);
        return;
      }

      // Desktop: navigate the already-open tab to the survey URL.
      if (surveyWindow && openUrl) {
        surveyWindow.location.href = openUrl;
      } else if (surveyWindow && !openUrl) {
        surveyWindow.close();
      }
      setSurveyTabHandle(surveyWindow);

      // Stash the RAW signed entry URL keyed by session so the waiting page's
      // reopen path can window.open the exact bytes. The ?url= param below
      // double-encodes the PS ps_s_hash signature and must NOT be the
      // navigation source — see
      // docs/investigations/ps-inbound-hash-live-error-2026-05-30.md.
      if (openUrl) setSurveyEntryUrl(waitingId, openUrl);

      const params = new URLSearchParams();
      // ?url= is kept only as a cold-load/refresh display + fallback signal;
      // the reopen navigation reads the lossless store, not this param.
      if (openUrl) params.set('url', openUrl);
      params.set('title', survey.title);
      params.set('minutes', String(survey.estimated_length_minutes));
      params.set('points', String(survey.points_value));
      if (didOpen) params.set('opened', '1');
      if (isPsSurvey) params.set('ps', '1');
      // Same as the same-tab path: remove the started survey from the wall
      // before navigating to the waiting page.
      onStarted?.(survey.id);
      navigate(`/survey/${waitingId}/waiting?${params.toString()}`);
    } catch (err) {
      // API failed — close the blank tab we opened so the member isn't
      // left staring at about:blank.
      surveyWindow?.close();
      const msg = err instanceof Error ? err.message : 'Failed to start survey';
      if (msg.includes('no longer available') || msg.includes('409')) {
        onSurveyUnavailable?.(survey.id);
        onClose();
      } else if (msg.includes('400')) {
        setStartError('Unable to start survey — please try again');
      } else {
        setStartError('Something went wrong — please try again');
      }
      setLaunching(false);
    }
  }

  function handleDismiss() {
    if (prescreenFailed && onDeny) onDeny();
    else onClose();
  }

  // ── Disqualified soft landing (T2) ──────────────────────────────────────────
  // Replaces the old amber auto-dismiss box. Normalizes the screen-out, shows
  // what the member keeps (consolation if any + protected streak), and offers a
  // one-tap path into the best next survey. No auto-close — the member leaves
  // on their own terms.
  if (prescreenFailed) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="survey-start-modal">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6 space-y-5" data-testid="prescreen-failed">
          <div className="flex justify-end">
            <button onClick={handleDismiss} className="text-[#6B7280] hover:text-[#111827]" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="text-center space-y-2">
            <div className="w-14 h-14 rounded-full bg-[#EEF2FF] flex items-center justify-center mx-auto">
              <Smile className="w-7 h-7 text-[#5B4FE8]" />
            </div>
            <h3 className="text-lg font-bold text-[#111827]">Not a match this time</h3>
            <p className="text-sm text-[#6B7280] leading-relaxed">
              Happens to everyone — it&apos;s not about you. Here&apos;s what you keep:
            </p>
          </div>

          {/* Stat tiles — consolation (if earned) + streak safe */}
          {(failConsolation > 0 || streakCurrent > 0) && (
            <div className="flex gap-2">
              {failConsolation > 0 && (
                <div className="flex-1 rounded-2xl bg-[#E6F7EE] p-3 text-center" data-testid="consolation-tile">
                  <p className="text-lg font-bold text-[#10B981] tabular-nums">+{failConsolation}</p>
                  <p className="text-[11px] text-[#059669] mt-0.5">pts for trying</p>
                </div>
              )}
              {streakCurrent > 0 && (
                <div className="flex-1 rounded-2xl bg-[#FFF7ED] p-3 text-center" data-testid="streak-tile">
                  <p className="text-lg font-bold text-[#EA580C] tabular-nums inline-flex items-center justify-center gap-1"><Flame className="w-4 h-4" /> {streakCurrent}</p>
                  <p className="text-[11px] text-[#C2410C] mt-0.5">streak safe</p>
                </div>
              )}
            </div>
          )}

          {/* Zero-consolation explanation (T7) */}
          {failConsolation === 0 && (
            <p className="text-xs text-[#6B7280] text-center leading-relaxed">
              No points this time — give the next survey a couple of minutes and you&apos;ll earn a few just for trying.
            </p>
          )}

          {/* Best next survey */}
          {bestNextSurvey && (
            <div className="space-y-2">
              <p className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide">Your best next survey</p>
              <div className="rounded-2xl bg-white border border-[#E5E7EB] shadow-sm p-4 space-y-3">
                <div className="flex items-center justify-between">
                  {(() => {
                    const tier = getMatchTier(bestNextSurvey.matchIr, bestNextSurvey.exchange);
                    return (
                      <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${matchTierPillClasses(tier.tone)}`}>
                        {tier.text}
                      </span>
                    );
                  })()}
                  <span className="flex items-center gap-1 text-xs text-[#9CA3AF]">
                    <Clock className="w-3.5 h-3.5" /> {getLoi(bestNextSurvey)} min
                  </span>
                </div>
                <p className="text-sm font-bold text-[#111827] leading-snug">
                  {bestNextSurvey.displayTitle} <span className="text-[#10B981]">${bestNextSurvey.cashValue}</span>
                </p>
                <button
                  onClick={handleTryNext}
                  data-testid="try-next-survey-btn"
                  className="w-full py-2.5 rounded-full bg-[#5B4FE8] text-white text-sm font-semibold hover:bg-[#4338CA] transition-colors flex items-center justify-center gap-1.5"
                >
                  Try this one <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          <button
            onClick={handleDismiss}
            className="block w-full text-center text-sm text-[#9CA3AF] hover:text-[#6B7280] transition-colors"
            data-testid="back-to-surveys"
          >
            Back to surveys
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="survey-start-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6 space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between">
          <h3 className="text-lg font-bold text-[#111827]">Ready to Start?</h3>
          <button onClick={handleDismiss} className="text-[#6B7280] hover:text-[#111827]" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Survey info */}
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#EEF2FF] flex items-center justify-center flex-shrink-0">
            <ClipboardList className="w-5 h-5 text-[#5B4FE8]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-[#111827] leading-snug">{survey.title}</p>
            <div className="flex items-center gap-4 mt-1.5">
              <span className="flex items-center gap-1 text-xs text-[#6B7280]">
                <Clock className="w-3.5 h-3.5" /> ~{survey.estimated_length_minutes} minutes
              </span>
              <span className="flex items-center gap-1 text-xs text-[#10B981] font-medium">
                <Coins className="w-3.5 h-3.5" /> {survey.points_value} pts (~${pointsUsd})
              </span>
              {instantCredit && (
                <span className="flex items-center gap-1 text-[11px] text-[#059669] font-medium">
                  <Check className="w-3 h-3" /> Credits instantly
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Inline qual gate (one at a time, exchange-agnostic) */}
        {hasQualGate && currentQual && !prescreenFailed && (
          <div className="space-y-3" data-testid="ps-prescreen-section">
            <p className="text-sm font-medium text-[#111827] flex items-center gap-1.5">
              <HelpCircle className="w-4 h-4 text-[#5B4FE8]" />
              Quick question before you begin:
            </p>
            <div className="bg-[#F8F9FA] rounded-xl p-4 border border-[#E5E7EB] space-y-3">
              <p className="text-sm text-[#111827] font-medium">{currentQual.question}</p>

              {/* Single select options */}
              {currentQual.options.length > 0 && !isMulti && (
                <div className={`flex flex-wrap gap-2 ${currentQual.options.length > 6 ? 'max-h-48 overflow-y-auto' : ''}`}>
                  {currentQual.options.map((opt) => (
                    <button
                      key={opt.code}
                      onClick={() => setSelectedCode(opt.code)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        selectedCode === opt.code
                          ? 'bg-[#5B4FE8] text-white border-[#5B4FE8]'
                          : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#5B4FE8]'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Multi-select options (checkboxes) */}
              {currentQual.options.length > 0 && isMulti && (
                <div className={`flex flex-wrap gap-2 ${currentQual.options.length > 6 ? 'max-h-48 overflow-y-auto' : ''}`}>
                  {currentQual.options.map((opt) => {
                    const checked = selectedCodes.includes(opt.code);
                    return (
                      <button
                        key={opt.code}
                        onClick={() => toggleMultiCode(opt.code)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors flex items-center gap-1.5 ${
                          checked
                            ? 'bg-[#EEF2FF] text-[#5B4FE8] border-[#5B4FE8]'
                            : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#5B4FE8]'
                        }`}
                      >
                        <span className={`w-4 h-4 rounded border flex items-center justify-center text-xs ${
                          checked ? 'bg-[#5B4FE8] border-[#5B4FE8] text-white' : 'border-[#D1D5DB]'
                        }`}>
                          {checked ? <Check className="w-3 h-3" /> : ''}
                        </span>
                        {opt.label}
                      </button>
                    );
                  })}
                  <p className="w-full text-xs text-[#9CA3AF] mt-1">Select all that apply</p>
                </div>
              )}

              {/* Range/open input (e.g. age, zipcode) */}
              {currentQual.options.length === 0 && (
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder={currentQual.type === 'range' ? 'Enter a number' : 'Enter your answer'}
                  value={selectedCode ?? ''}
                  onChange={(e) => setSelectedCode(e.target.value || null)}
                  className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#5B4FE8]"
                />
              )}
            </div>
            <button
              onClick={() => void handleQualGateAnswer()}
              disabled={!hasAnswer || submittingPrescreen}
              className="w-full py-2.5 rounded-full bg-[#5B4FE8] text-white text-sm font-medium disabled:opacity-40 hover:bg-[#4338CA] transition-colors flex items-center justify-center gap-1.5"
            >
              {submittingPrescreen ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {submittingPrescreen ? 'Checking...' : 'Continue'}
            </button>
            {/* Skip is deliberately rare. Rather than silently routing the
                member into a likely screen-out, "can't answer" reveals a
                choice that prefers finding a better match; "Try anyway" is the
                quiet secondary, with the expectation set up front. */}
            {!skipExpanded ? (
              <button
                type="button"
                onClick={() => setSkipExpanded(true)}
                className="block w-full text-center text-sm text-gray-400 hover:text-gray-600"
                data-testid="qual-gate-skip"
              >
                I can&apos;t answer this
              </button>
            ) : (
              <div className="rounded-xl bg-[#F8F9FA] border border-[#E5E7EB] p-3 space-y-2" data-testid="qual-gate-skip-choice">
                <p className="text-xs text-[#6B7280] leading-relaxed">
                  Without this we can&apos;t confirm you&apos;re a match. We&apos;d rather find you a better one — or you can try anyway (you may be screened out, but you&apos;ll still earn a few points for trying).
                </p>
                <button
                  type="button"
                  onClick={handleFindBetterMatch}
                  className="w-full py-2 rounded-full bg-[#5B4FE8] text-white text-sm font-medium hover:bg-[#4338CA] transition-colors"
                  data-testid="qual-gate-find-better"
                >
                  Find me a better match
                </button>
                <button
                  type="button"
                  onClick={handleQualGateSkip}
                  className="block w-full text-center text-xs text-gray-400 underline hover:text-gray-600"
                  data-testid="qual-gate-try-anyway"
                >
                  Try anyway &rarr;
                </button>
              </div>
            )}
          </div>
        )}

        {/* Legacy prescreen (category yes/no) */}
        {hasLegacyPrescreen && legacyStatement && !prescreenFailed && (
          <div className="space-y-3" data-testid="prescreen-section">
            <p className="text-sm font-medium text-[#111827]">Quick question before you begin:</p>
            <div className="bg-[#F8F9FA] rounded-xl p-4 border border-[#E5E7EB] space-y-3">
              <p className="text-sm text-[#111827] font-medium" data-testid="prescreen-question">{legacyStatement}?</p>
              <div className="flex gap-2">
                <button onClick={() => handleLegacySelect(true)} data-testid="prescreen-yes"
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${legacyAnswer === true ? 'bg-[#5B4FE8] text-white border-[#5B4FE8]' : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#5B4FE8]'}`}>Yes</button>
                <button onClick={() => handleLegacySelect(false)} data-testid="prescreen-no"
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${legacyAnswer === false ? 'bg-[#5B4FE8] text-white border-[#5B4FE8]' : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#5B4FE8]'}`}>No</button>
              </div>
            </div>
          </div>
        )}

        {/* Start error (e.g. 409 survey unavailable) */}
        {startError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
            <p className="text-sm text-red-800 font-medium">{startError}</p>
          </div>
        )}

        {/* Profiling question — shown when no PS prescreen and question available */}
        {!prescreenFailed && !hasQualGate && !hasLegacyPrescreen && prescreenQuestion && !profileAnswered && (
          <div className="space-y-3" data-testid={`prescreen-type-${prescreenQuestion.questionType}`}>
            <p className="text-xs text-[#5B4FE8] font-medium">Quick question to unlock more surveys ({prescreenQuestion.surveysUnlocked} could open up)</p>
            <div className="bg-[#F8F9FA] rounded-xl p-4 border border-[#E5E7EB] space-y-3">
              <p className="text-sm text-[#111827] font-medium">{prescreenQuestion.questionText}</p>
              {prescreenQuestion.questionType === 'claude_buckets' ? (
                <div data-testid="claude-buckets" className="space-y-2">
                  {bucketsLoading && (
                    <div className="flex items-center gap-2 text-sm text-[#6B7280]">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading options...
                    </div>
                  )}
                  {!bucketsLoading && claudeBuckets && claudeBuckets.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {claudeBuckets.map((b, idx) => (
                        <button
                          key={`${b.label}-${idx}`}
                          onClick={() => {
                            setSelectedBucketIdx(idx);
                            setProfileAnswer(b.codes.join(','));
                          }}
                          className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                            selectedBucketIdx === idx
                              ? 'bg-[#5B4FE8] text-white border-[#5B4FE8]'
                              : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#5B4FE8]'
                          }`}
                        >
                          {b.icon ? <span className="mr-1">{b.icon}</span> : null}
                          {b.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : prescreenQuestion.questionType === 'multi_select' ? (
                <div className="space-y-2" data-testid="multi-select">
                  <div className={`flex flex-wrap gap-2 ${prescreenQuestion.options.length > 6 ? 'max-h-48 overflow-y-auto' : ''}`}>
                    {prescreenQuestion.options.map((opt) => {
                      const checked = multiSelectedValues.includes(opt.value);
                      return (
                        <button
                          key={opt.value}
                          onClick={() => {
                            // Mutually-exclusive "None of the above": clears
                            // every other selection when chosen.
                            if (opt.value === 'none') {
                              const next = checked ? [] : ['none'];
                              setMultiSelectedValues(next);
                              setProfileAnswer(next.join(','));
                              return;
                            }
                            const without = multiSelectedValues.filter((v) => v !== 'none');
                            const next = without.includes(opt.value)
                              ? without.filter((v) => v !== opt.value)
                              : [...without, opt.value];
                            setMultiSelectedValues(next);
                            setProfileAnswer(next.length > 0 ? next.join(',') : null);
                          }}
                          className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors flex items-center gap-1.5 ${
                            checked
                              ? 'bg-[#EEF2FF] text-[#5B4FE8] border-[#5B4FE8]'
                              : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#5B4FE8]'
                          }`}
                        >
                          <span className={`w-4 h-4 rounded border flex items-center justify-center text-xs ${
                            checked ? 'bg-[#5B4FE8] border-[#5B4FE8] text-white' : 'border-[#D1D5DB]'
                          }`}>
                            {checked ? <Check className="w-3 h-3" /> : ''}
                          </span>
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-[#9CA3AF]">Select all that apply</p>
                </div>
              ) : prescreenQuestion.questionType === 'likert' ? (
                <div className="space-y-2" data-testid="likert-scale">
                  <div className="grid grid-cols-5 gap-1.5">
                    {prescreenQuestion.options.map((opt, idx) => {
                      const active = profileAnswer === opt.value;
                      return (
                        <button
                          key={opt.value}
                          onClick={() => setProfileAnswer(opt.value)}
                          aria-label={opt.label}
                          title={opt.label}
                          className={`py-3 rounded-lg border text-xs font-medium transition-colors flex flex-col items-center gap-0.5 ${
                            active
                              ? 'bg-[#5B4FE8] text-white border-[#5B4FE8]'
                              : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#5B4FE8]'
                          }`}
                        >
                          <span className="text-base font-semibold">{idx + 1}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex justify-between text-[11px] text-[#9CA3AF] px-0.5">
                    <span>{prescreenQuestion.options[0]?.label ?? 'Strongly Disagree'}</span>
                    <span>{prescreenQuestion.options[prescreenQuestion.options.length - 1]?.label ?? 'Strongly Agree'}</span>
                  </div>
                </div>
              ) : (
                <div className={`flex flex-wrap gap-2 ${prescreenQuestion.options.length > 6 ? 'max-h-48 overflow-y-auto' : ''}`}>
                  {prescreenQuestion.options.map((opt) => (
                    <button key={opt.value} onClick={() => setProfileAnswer(opt.value)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        profileAnswer === opt.value ? 'bg-[#5B4FE8] text-white border-[#5B4FE8]' : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#5B4FE8]'
                      }`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={() => {
                if (profileAnswer) {
                  if (prescreenQuestion.questionType === 'claude_buckets' && selectedBucketIdx !== null && claudeBuckets) {
                    const bucket = claudeBuckets[selectedBucketIdx];
                    if (bucket) onPrescreenAnswer?.(prescreenQuestion.qualCode, bucket.codes, bucket.label, prescreenQuestion.attributeKey);
                  } else if (prescreenQuestion.questionType === 'multi_select') {
                    const selected = prescreenQuestion.options.filter((o) => multiSelectedValues.includes(o.value));
                    if (selected.length > 0) {
                      const codes = selected.flatMap((o) => o.conditionCodes);
                      const value = selected.map((o) => o.value).join(',');
                      onPrescreenAnswer?.(prescreenQuestion.qualCode, codes, value, prescreenQuestion.attributeKey);
                    }
                  } else {
                    const opt = prescreenQuestion.options.find((o) => o.value === profileAnswer);
                    if (opt) onPrescreenAnswer?.(prescreenQuestion.qualCode, opt.conditionCodes, opt.value, prescreenQuestion.attributeKey);
                  }
                }
                setProfileAnswered(true);
              }} disabled={!profileAnswer}
                className="flex-1 py-2 rounded-full bg-[#5B4FE8] text-white text-sm font-medium disabled:opacity-40">
                Save &amp; continue
              </button>
              <button onClick={() => { onPrescreenSkip?.(prescreenQuestion.qualCode); setProfileAnswered(true); }}
                className="text-sm text-[#6B7280] hover:text-[#111827] px-3">Skip</button>
            </div>
          </div>
        )}

        {/* Profile enrichment (TYPE 2) — non-blocking, fetched internally
            from the new frequency-driven /surveys/profile-question endpoint
            when no parent-supplied prescreenQuestion is present. */}
        {showEnrichment && enrichmentQ && (
          <div className="space-y-3" data-testid="profile-enrichment-section">
            <p className="text-xs text-[#5B4FE8] font-medium">Quick question to help us match you better</p>
            <div className="bg-[#F8F9FA] rounded-xl p-4 border border-[#E5E7EB] space-y-3">
              <p className="text-sm text-[#111827] font-medium">{enrichmentQ.questionText}</p>
              {enrichmentQ.inputType === 'multi_select' ? (
                <div className="space-y-2">
                  <div className={`flex flex-wrap gap-2 ${enrichmentQ.options.length > 6 ? 'max-h-48 overflow-y-auto' : ''}`}>
                    {enrichmentQ.options.map((opt) => {
                      const checked = enrichmentMulti.includes(opt.value);
                      return (
                        <button
                          key={opt.value}
                          onClick={() => {
                            if (opt.value === 'none') {
                              setEnrichmentMulti(checked ? [] : ['none']);
                              return;
                            }
                            const without = enrichmentMulti.filter((v) => v !== 'none');
                            const next = without.includes(opt.value)
                              ? without.filter((v) => v !== opt.value)
                              : [...without, opt.value];
                            setEnrichmentMulti(next);
                          }}
                          className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors flex items-center gap-1.5 ${
                            checked
                              ? 'bg-[#EEF2FF] text-[#5B4FE8] border-[#5B4FE8]'
                              : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#5B4FE8]'
                          }`}
                        >
                          <span className={`w-4 h-4 rounded border flex items-center justify-center text-xs ${
                            checked ? 'bg-[#5B4FE8] border-[#5B4FE8] text-white' : 'border-[#D1D5DB]'
                          }`}>
                            {checked ? <Check className="w-3 h-3" /> : ''}
                          </span>
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-[#9CA3AF]">Select all that apply</p>
                </div>
              ) : (
                <div className={`flex flex-wrap gap-2 ${enrichmentQ.options.length > 6 ? 'max-h-48 overflow-y-auto' : ''}`}>
                  {enrichmentQ.options.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setEnrichmentValue(opt.value)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        enrichmentValue === opt.value
                          ? 'bg-[#5B4FE8] text-white border-[#5B4FE8]'
                          : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#5B4FE8]'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => void handleEnrichmentAnswer()}
              disabled={enrichmentQ.inputType === 'multi_select' ? enrichmentMulti.length === 0 : !enrichmentValue}
              className="w-full py-2.5 rounded-full bg-[#5B4FE8] text-white text-sm font-medium disabled:opacity-40 hover:bg-[#4338CA]"
              data-testid="enrichment-save"
            >
              Save &amp; continue
            </button>
            <button
              type="button"
              onClick={handleEnrichmentSkip}
              className="block w-full text-center text-sm text-gray-400 underline hover:text-gray-600"
              data-testid="enrichment-skip"
            >
              Skip for now &rarr;
            </button>
          </div>
        )}

        {/* Expectation set after the member chose to try a survey they
            couldn't confirm a match for (T7). */}
        {skippedQualGate && !prescreenFailed && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3" data-testid="qual-gate-skip-note">
            <p className="text-xs text-amber-800 leading-relaxed">
              You may be screened out of this one — you&apos;ll still earn a few points for trying.
            </p>
          </div>
        )}

        {/* Tips — shown when no active prescreen */}
        {!prescreenFailed && !hasQualGate && !showEnrichment && (profileAnswered || !prescreenQuestion) && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-[#6B7280] uppercase tracking-wide">A few things to know:</p>
            <ul className="space-y-1.5 text-sm text-[#6B7280]">
              <li className="flex items-start gap-2"><span className="mt-0.5">&bull;</span>{categoryTip}</li>
              <li className="flex items-start gap-2"><span className="mt-0.5">&bull;</span>Complete in one sitting if possible</li>
              <li className="flex items-start gap-2"><span className="mt-0.5">&bull;</span>Don't use the browser back button during the survey</li>
            </ul>
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3 pt-1">
          <button onClick={handleDismiss}
            className="flex-1 py-3 rounded-full border-2 border-[#E5E7EB] text-sm font-medium text-[#6B7280] hover:bg-gray-50 transition-colors">
            {prescreenFailed ? 'Not for me' : 'Maybe Later'}
          </button>
          {!prescreenFailed && !hasQualGate && (
            <button onClick={() => void handleStart()} disabled={!canStart || launching} data-testid="start-survey-btn"
              className="flex-1 py-3 rounded-full bg-[#5B4FE8] text-sm font-medium text-white hover:bg-[#4338CA] transition-colors disabled:opacity-40 flex items-center justify-center gap-1.5">
              {launching ? <><Loader2 className="w-4 h-4 animate-spin" /> Starting...</> : <>Start Survey <ArrowRight className="w-4 h-4" /></>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function isPsSurveyCheck(survey: RankedSurvey): boolean {
  return survey.exchange === 'ps' || survey.exchange == null || survey.provider_name === 'PureSpectrum';
}
